import * as yup from 'yup';

export const TRIGGER_TYPE_OPTIONS = [
    {
        value: 'forecast and observation',
        label: 'Prévision et observation',
    },
    {
        value: 'forecast',
        label: 'Prévision',
    },
    {
        value: 'observation',
        label: 'Observation',
    },
];

export const DATA_SOURCE_OPTIONS = [
    { value: 'ecmwf', label: 'ECMWF' },
    { value: 'meteomatics', label: 'Meteomatics' },
    { value: 'meteofrance', label: 'Météo-France' },
    { value: 'meteostat', label: 'Meteostat' },
];

export const UNIT_OPTIONS = [
    { value: 'mm', label: 'mm' },
    { value: 'mm/h', label: 'mm/h' },
    { value: '°c', label: '°C' },
    { value: 'km/h', label: 'km/h' },
];

const ALLOWED_TRIGGER_TYPES = TRIGGER_TYPE_OPTIONS.map((opt) => opt.value);

const decimalString = (label) =>
    yup
        .string()
        .transform((value) => (value == null || String(value).trim() === '' ? undefined : String(value).trim()))
        .test('is-decimal', `${label} doit être un nombre valide`, (value) => {
            if (value == null || value === '') return true;
            const normalized = value.replace(',', '.');
            return !Number.isNaN(Number(normalized));
        });

/**
 * Schema yup aligné sur SubCoverageCreate (backend).
 * Règle timing_day centralisée selon trigger_type.
 */
export const subCoverageCreateSchema = yup.object().shape({
    partner_id: yup.string().required('Veuillez sélectionner un partenaire'),
    reference_id: yup.string().required('Veuillez sélectionner une référence'),

    trigger_type: yup
        .string()
        .required('Le type de déclenchement est obligatoire')
        .test(
            'allowed-trigger-type',
            'Le type de déclenchement doit être « Prévision », « Observation » ou « Prévision et observation »',
            (value) => ALLOWED_TRIGGER_TYPES.includes(String(value || '').toLowerCase())
        ),

    data_source: yup.string().trim().required('La source des données est obligatoire'),

    unit: yup.string().trim().required("L'unité de mesure est obligatoire"),

    trigger_low: decimalString('Le seuil inférieur'),
    trigger_sup: decimalString('Le seuil supérieur'),
    prcp_trigger_hour_inf: decimalString("L'heure de début"),
    trigger_hour_sup: decimalString("L'heure de fin"),

    prcp_trigger_number_hours: yup
        .number()
        .transform((value, original) =>
            original === '' || original == null || Number.isNaN(value) ? undefined : value
        )
        .nullable()
        .optional()
        .integer('Le nombre d’heures doit être un entier')
        .min(0, 'Le nombre d’heures doit être supérieur ou égal à 0'),

    timing_hour: yup
        .number()
        .transform((value, original) =>
            original === '' || original == null || Number.isNaN(value) ? undefined : value
        )
        .nullable()
        .optional()
        .integer('L’heure du déclenchement doit être un entier')
        .min(0, 'L’heure du déclenchement doit être entre 0 et 23')
        .max(23, 'L’heure du déclenchement doit être entre 0 et 23'),

    timing_day: yup
        .number()
        .transform((value, original) =>
            original === '' || original == null || Number.isNaN(value) ? undefined : value
        )
        .typeError('Le jour du déclenchement doit être un entier')
        .required('Le jour du déclenchement est obligatoire')
        .integer('Le jour du déclenchement doit être un entier')
        .test(
            'timing-day-by-trigger-type',
            'Jour de déclenchement invalide pour ce type de déclenchement',
            function timingDayRule(value) {
                if (value == null || Number.isNaN(value)) return true;
                const triggerType = String(this.parent.trigger_type || '').toLowerCase();
                if (triggerType === 'forecast and observation' || triggerType === 'forecast') {
                    if (value > 0) {
                        return this.createError({
                            message:
                                'Pour « Prévision » / « Prévision et observation », le jour de déclenchement doit être ≤ 0',
                        });
                    }
                    return true;
                }
                if (triggerType === 'observation') {
                    if (value !== 0) {
                        return this.createError({
                            message: 'Pour « Observation », le jour de déclenchement doit être 0',
                        });
                    }
                    return true;
                }
                return true;
            }
        ),

    observation_timing_hour: yup
        .number()
        .transform((value, original) =>
            original === '' || original == null || Number.isNaN(value) ? undefined : value
        )
        .typeError('L’heure d’observation doit être un entier')
        .required('L’heure d’observation est obligatoire')
        .integer('L’heure d’observation doit être un entier')
        .min(0, 'L’heure d’observation doit être entre 0 et 23')
        .max(23, 'L’heure d’observation doit être entre 0 et 23'),

    observation_timing_day: yup
        .string()
        .trim()
        .required('Le jour d’observation est obligatoire')
        .test(
            'observation-timing-day-format',
            'Le jour d’observation doit être un entier (ex. 0, +1, +2)',
            (value) => {
                if (!value) return false;
                return /^\+?\d+$/.test(String(value).trim());
            }
        ),

    siv_ratio_maintained: yup
        .string()
        .required('Le taux SIV maintenu est obligatoire')
        .test('siv-maintained-range', 'La valeur doit être comprise entre 0 et 100 %.', (value) => {
            if (value == null || value === '') return false;
            const num = Number(String(value).replace(',', '.'));
            return !Number.isNaN(num) && num >= 0 && num <= 100;
        }),

    siv_ratio_cancelled: yup
        .string()
        .required('Le taux SIV annulé est obligatoire')
        .test('siv-cancelled-range', 'La valeur doit être comprise entre 0 et 100 %.', (value) => {
            if (value == null || value === '') return false;
            const num = Number(String(value).replace(',', '.'));
            return !Number.isNaN(num) && num >= 0 && num <= 100;
        }),

    orakle_price_ratio_excl_tsca: yup
        .string()
        .required('Le ratio de prix hors TSCA est obligatoire')
        .test('orakle-decimal', 'Le ratio de prix hors TSCA doit être un nombre valide', (value) => {
            if (value == null || value === '') return false;
            return !Number.isNaN(Number(String(value).replace(',', '.')));
        }),

    sub_coverage_start_date: yup
        .string()
        .required('La date de début est obligatoire'),

    sub_coverage_end_date: yup
        .string()
        .required('La date de fin est obligatoire')
        .test(
            'dates-order',
            'La date de fin doit être postérieure à la date de début.',
            function datesOrder(endDate) {
                const startDate = this.parent.sub_coverage_start_date;
                if (!startDate || !endDate) return true;
                return new Date(startDate) < new Date(endDate);
            }
        ),

    city: yup
        .string()
        .trim()
        .required('La ville est obligatoire')
        .min(2, 'La ville doit contenir au moins 2 caractères')
        .max(50, 'La ville ne peut pas dépasser 50 caractères'),

    country: yup
        .string()
        .trim()
        .required('Le pays est obligatoire')
        .min(2, 'Le pays doit contenir au moins 2 caractères')
        .max(50, 'Le pays ne peut pas dépasser 50 caractères'),

    activity_address: yup
        .string()
        .trim()
        .required('L’adresse de l’activité est obligatoire')
        .min(5, 'L’adresse doit contenir au moins 5 caractères')
        .max(255, 'L’adresse ne peut pas dépasser 255 caractères'),

    zip_code: yup
        .string()
        .transform((value) => (value == null || value === '' ? undefined : value))
        .nullable()
        .optional()
        .min(5, 'Le code postal doit contenir au moins 5 caractères')
        .max(10, 'Le code postal ne peut pas dépasser 10 caractères'),

    geo_latitude: decimalString('La latitude'),
    geo_longitude: decimalString('La longitude'),
});

export const defaultSubCoverageFormValues = {
    partner_id: '',
    reference_id: '',
    trigger_type: 'forecast and observation',
    data_source: '',
    unit: 'mm/h',
    trigger_low: '',
    trigger_sup: '',
    prcp_trigger_hour_inf: '',
    trigger_hour_sup: '',
    prcp_trigger_number_hours: '',
    timing_hour: '',
    timing_day: '0',
    observation_timing_hour: '',
    observation_timing_day: '0',
    siv_ratio_maintained: '',
    siv_ratio_cancelled: '',
    orakle_price_ratio_excl_tsca: '',
    sub_coverage_start_date: '',
    sub_coverage_end_date: '',
    city: '',
    country: '',
    activity_address: '',
    zip_code: '',
    geo_latitude: '',
    geo_longitude: '',
};
